/* eslint-disable react-hooks/exhaustive-deps */
// TODO: Better dependency pattern to avoid ^
import React, {
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useEffect,
} from 'react';
import { User } from '../models/user';
import { useAuth } from './useAuth';
import { useFetchUserData } from './useFetchUserData';
import { useCoach } from '../api/healthCoach/useCoach';
import { RawScoreDataPoint } from '../models/score';
import { useUserDiagnosis } from '../api/healthCoach/useUserDiagnosis';
import { UserDiagnosis } from '../api/types';
import useUserAybleScores from '../api/healthCoach/useUserAybleScores';
import { computeAybleScores } from '../utils/scores';

type State = {
  all_scores_data: RawScoreDataPoint[];
  ayble_score: string[];
  current_subphase: any;
  loading: boolean;
  lowest_ayble_score: string;
  over_time_score: string;
  stress_score: string[];
  today_score: string;
  user: User | null;
  userDiagnosis: UserDiagnosis[];
};

type Action =
  | {
      all_scores_data: any[];
      ayble_score: any[];
      current_subphase: any;
      lowest_ayble_score: string;
      over_time_score: string;
      stress_score: string[];
      today_score: string;
      type: 'initUserData';
      user: User;
      userDiagnosis: UserDiagnosis[];
    }
  | {
      type: 'startFetchingUser';
    };

type Dispatch = (action: Action) => void;

type ContextType = { state: State; dispatch: Dispatch } | undefined;
export const DashboardContext = createContext<ContextType>(undefined);

function actionsReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'startFetchingUser': {
      return {
        ...state,
        loading: true,
      };
    }

    case 'initUserData': {
      return {
        all_scores_data: action.all_scores_data,
        ayble_score: action.ayble_score,
        current_subphase: action.current_subphase,
        loading: false,
        lowest_ayble_score: action.lowest_ayble_score,
        over_time_score: action.over_time_score,
        stress_score: action.stress_score,
        today_score: action.today_score,
        user: action.user,
        userDiagnosis: action.userDiagnosis,
      };
    }

    default: {
      throw new Error(`Unhandled action type: ${(action as any).type}`);
    }
  }
}

type Props = {
  children: ReactNode;
  patientId?: string;
};

/**
 * This state provider handles both cases:
 * 1. state of a self user (regular user logged in the app)
 * 2. state of a specific given user when passing the patient id for the health coach dashboard.
 */
export function DashboardPageStateProvider({ children, patientId }: Props) {
  const initialState: State = {
    all_scores_data: [],
    ayble_score: [],
    current_subphase: null,
    loading: true,
    lowest_ayble_score: '0',
    over_time_score: '0',
    stress_score: [],
    today_score: '0',
    user: null,
    userDiagnosis: [],
  };

  const { loading: isAuthenticating, userToken } = useAuth();
  const { aybleScores } = useUserAybleScores();
  const { getUserAybleScoreSummary } = useFetchUserData();
  const { data: userDiagnosis, isLoading: isFetchingDiagnosis } =
    useUserDiagnosis(patientId);

  const {
    fetchUser,
    loading: isCoachAuthenticating,
    isAuthenticated: isCoachAuthenticated,
  } = useCoach();

  const [state, dispatch] = useReducer(actionsReducer, initialState);
  const value = { state, dispatch };

  useEffect(() => {
    const fetchUserData = async () => {
      dispatch({ type: 'startFetchingUser' });

      const fetchedPatient = await fetchUser(patientId as string);
      const { lowest_ayble_score } = await getUserAybleScoreSummary(patientId);
      const {
        ayble_score,
        stress_score,
        over_time_score,
        today_score,
        rawData,
      } = computeAybleScores(aybleScores);

      dispatch({
        // add this once coach endpoint can return such info for any user
        all_scores_data: rawData,
        ayble_score,
        current_subphase: null,
        lowest_ayble_score,
        over_time_score,
        stress_score,
        today_score,
        type: 'initUserData',
        user: {
          ...fetchedPatient,
          first_name: fetchedPatient.first_name || '--',
          last_name: fetchedPatient.last_name || '--',
        },
        userDiagnosis: userDiagnosis ?? [],
      });
    };

    const shouldFetchUser =
      !!patientId &&
      !isCoachAuthenticating &&
      isCoachAuthenticated &&
      !isFetchingDiagnosis;

    if (shouldFetchUser) {
      fetchUserData();
    }
  }, [
    aybleScores,
    isAuthenticating,
    isFetchingDiagnosis,
    isCoachAuthenticating,
    userDiagnosis,
    userToken,
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboardPageState() {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error(
      'useDashboardPageState must be used within an DashboardContextProvider'
    );
  }

  return context;
}
